<template>
  <section
    class="labeled-section"
    :class="{
      'labeled-section--no-size-context': noSizeContext,
      'labeled-section--wide': wide,
      'labeled-section--border-top': borderTop,
    }"
  >
    <v-row
      no-gutters
      :class="[
        `labeled-section-label ${display.mdAndUp.value ? 'pa-2' : 'pa-7'}`,
        backgroundColor ? `bg-${backgroundColor}` : '',
        titleSize === 'small' ? 'labeled-section-label--small-title' : '',
      ]"
      justify="space-between"
      align="start"
    >
      <div class="labeled-section-main-header">
        <slot name="header">
          <h1>
            <v-icon
              v-if="icon"
              :icon="icon"
              size="30"
              class="mb-2 mr-2 ml-2"
              :color="backgroundColor === 'black' ? 'white' : iconColor"
              aria-hidden="true"
            />
            <slot name="title">{{ title }}</slot>
            {{ subtitle ? "" : "" }}
            <slot name="subtitle">
              <small v-if="subtitle">{{ subtitle }}</small>
            </slot>
          </h1>
        </slot>
        <div class="d-md-none">
          <v-divider
            v-if="topRightText || bottomRightText || $slots.right"
            class="my-2"
          />
          <div class="d-flex justify-space-between align-center mt-2">
            <div>
              <div v-if="topRightText">
                {{ topRightText }}
              </div>
              <div v-if="bottomRightText">
                {{ bottomRightText }}
              </div>
              <slot name="right" />
            </div>
            <v-btn
              icon
              variant="text"
              @click="toggleCart"
              class="ml-2"
              v-if="showCartIcon"
              role="button"
              tabindex="0"
              :aria-label="`${!props.cartOpen ? 'Open' : 'Close'} shopping cart`"
              :title="`${!props.cartOpen ? 'Open' : 'Close'} shopping cart`"
            >
              <v-badge
                :content="cartBadgeCount"
                color="primary"
                floating
                v-if="cartBadgeCount > 0"
                aria-hidden="true"
              >
                <v-icon aria-hidden="true">fas fa-shopping-cart</v-icon>
              </v-badge>
              <v-icon v-else aria-hidden="true">fas fa-shopping-cart</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      <div class="d-none d-md-flex align-center">
        <div class="mr-4 text-right">
          <div v-if="topRightText">
            {{ topRightText }}
          </div>
          <div v-if="bottomRightText">
            {{ bottomRightText }}
          </div>
          <slot name="right" />
        </div>
        <v-btn
          icon
          variant="text"
          @click="toggleCart"
          v-if="showCartIcon"
          role="button"
          tabindex="0"
          :aria-label="`${!props.cartOpen ? 'Open' : 'Close'} shopping cart`"
          :title="`${!props.cartOpen ? 'Open' : 'Close'} shopping cart`"
        >
          <v-badge
            :content="cartBadgeCount"
            color="primary"
            floating
            v-if="cartBadgeCount > 0"
            aria-hidden="true"
          >
            <v-icon aria-hidden="true">fas fa-shopping-cart</v-icon>
          </v-badge>
          <v-icon v-else aria-hidden="true">fas fa-shopping-cart</v-icon>
        </v-btn>
      </div>
    </v-row>

    <v-row no-gutters class="labeled-section-caption" v-if="$slots.caption">
      <slot name="caption" />
    </v-row>

    <div class="labeled-section-content" v-if="$slots.default">
      <slot />
    </div>
  </section>
</template>

<script setup lang="ts">
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import { buildSeriesTicketGroups } from "@common/purchase/series-cart";
import { useDisplay } from "vuetify";

const display = useDisplay();

const props = withDefaults(
  defineProps<{
    title?: string;
    icon?: string;
    subtitle?: string;
    wide?: boolean;
    borderTop?: boolean;
    noSizeContext?: boolean;
    topRightText?: string;
    bottomRightText?: string;
    backgroundColor?: string;
    iconColor?: string;
    titleSize?: "small" | "normal";
    purchase: TicketPurchaseDtoViewModel;
    showCartIcon?: boolean;
    cartOpen?: boolean;
  }>(),
  {
    wide: false,
    borderTop: false,
    noSizeContext: false,
    cartOpen: false,
    titleSize: "normal",
    iconColor: "primary",
    showCartIcon: false,
  },
);

// Cart badge count. Single-date events count tickets directly. Series create one ticket per
// included day, so tickets.length over-counts (a 4-day pass = 4 tickets); count passes instead so
// the badge matches the pass row ("× N") and the quantity selector.
const cartBadgeCount = computed(() => {
  if (props.purchase?.eventSeriesId != null) {
    return buildSeriesTicketGroups(
      props.purchase.tickets ?? [],
      props.purchase.eventSeries?.passTypes ?? [],
    ).reduce((sum, g) => sum + g.passQty, 0);
  }
  return props.purchase?.tickets?.length || 0;
});

const emit = defineEmits<{
  (e: "update:cartOpen", value: boolean): void;
}>();

function toggleCart() {
  emit("update:cartOpen", !props.cartOpen);
}
</script>

<style lang="scss">
.labeled-section {
  margin-bottom: 16px !important;

  &.labeled-section--border-top {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    padding-top: 16px;
  }

  ~ .labeled-section {
    margin-top: 44px;
  }

  color: #333;

  .labeled-section-label {
    position: relative;
    &--small-title h1 {
      font-size: 1.25rem;
    }
  }

  .labeled-section-main-header {
    flex-grow: 1;
    min-width: 0;
    h1 {
      word-break: break-word;
    }
  }

  .labeled-section-caption {
    font-style: italic;
    font-size: 0.75rem;
    width: 100%;
    padding: 0 8px;
  }

  .labeled-section-content {
    margin-top: 18px;
    padding: 0 8px;
    .labeled-section:not(.labeled-section--no-size-context) & {
      margin-top: 12px;
      .labeled-section:not(.labeled-section--no-size-context) & {
        margin-top: 10px;
      }
    }
    > .c-loader-status.has-progress-placeholder:first-child {
      margin-top: -10px;
    }
  }
}

.cart-drawer {
  .v-card-title {
    font-size: 1.1rem;
    font-weight: 500;
  }
  .scrollable-cart-items {
    overflow-y: auto;
  }
  .cart-header {
    background-color: #f9f9f9;
    border-bottom: 1px solid #e0e0e0;
  }
  .cart-category-header {
    background-color: #f5f5f5;
    color: #555;
  }
  .cart-item {
    min-height: auto;
    &:not(:last-child) {
      border-bottom: 1px solid #eee;
    }
  }
  .cart-footer {
    border-top: 1px solid #e0e0e0;
    background-color: #fdfdfd;
    .total-line {
      line-height: 1.5;
    }
  }
}
</style>
